import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { PaginationFilter } from '../../interfaces';
import { BasePaginatorComponent } from '../base-paginator/base-paginator-component';
import { IconSvgComponent } from '../icon/svg-icon.component';
import { IconsConstants } from '../../common';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { NumberFormatPipe } from '../../pipes';
import { CheckNumberOfDigitsPipe } from '../../pipes/check-digits-number-pipe';

@Component({
  selector: 'app-paginator',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatSelectModule,
    TranslateModule,
    IconSvgComponent,
    NumberFormatPipe,
    CheckNumberOfDigitsPipe,
  ],
  templateUrl: './paginator.component.html',
  styleUrls: ['./paginator.component.scss'],
})
export class PaginatorComponent extends BasePaginatorComponent implements OnInit, OnChanges {
  totalPages: number = 0;
  renderedPages: number[] = [];
  @Input() length: number = 0;
  @Input() pageButtonsToShow: number = 3;
  @Output() paginationFilter: EventEmitter<PaginationFilter> = new EventEmitter<PaginationFilter>();
  
  protected readonly IconsConstants = IconsConstants;

  constructor(injector: Injector) {
    super(['PAGINATOR'], injector);
  }

  ngOnInit(): void {
    this.getPaginatorConfigsFromURL();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes && changes['length']) {
      this.managePaginationButtons(this.pageIndex);
    }
  }

  onClickNextPage(): void {
    if (this.pageIndex < this.totalPages - 1) {
      this.pageIndex += 1;
      this.managePaginationButtons(this.pageIndex);
      this.setPaginatorConfigsFromURL();
      this.paginationFilter.emit({ pageIndex: this.pageIndex, pageSize: this.pageSize });
    }
  }

  onClickPreviousPage(): void {
    if (this.pageIndex > 0) {
      this.pageIndex -= 1;
      this.managePaginationButtons(this.pageIndex);
      this.setPaginatorConfigsFromURL();
      this.paginationFilter.emit({ pageIndex: this.pageIndex, pageSize: this.pageSize });
    }
  }

  onClickFirstPage(): void {
    this.pageIndex = 0;
    this.managePaginationButtons(this.pageIndex);
    this.setPaginatorConfigsFromURL();
    this.paginationFilter.emit({ pageIndex: this.pageIndex, pageSize: this.pageSize });
  }

  onClickLastPage(): void {
    this.pageIndex = Math.floor(this.length / this.pageSize);
    this.managePaginationButtons(this.pageIndex);
    this.setPaginatorConfigsFromURL();
    this.paginationFilter.emit({ pageIndex: this.pageIndex, pageSize: this.pageSize });
  }

  onClickPage(pageNumber: number): void {
    this.pageIndex = pageNumber - 1;
    this.managePaginationButtons(this.pageIndex);
    this.setPaginatorConfigsFromURL();
    this.paginationFilter.emit({ pageIndex: this.pageIndex, pageSize: this.pageSize }); 
  }

  onSelectPageSize(select: MatSelectChange): void {
    this.pageSize = select.value;
    this.totalPages = Math.floor(this.length / this.pageSize);
    if(this.pageIndex > this.totalPages) {
      this.pageIndex = this.totalPages;
    }
    this.managePaginationButtons(this.pageIndex);
    this.setPaginatorConfigsFromURL();
    this.paginationFilter.emit({ pageIndex: this.pageIndex, pageSize: select.value });
  }

  private managePaginationButtons(currentPage: number): number[] {
    this.totalPages = Math.ceil(this.length / this.pageSize);
    const shouldShowAllPages = this.totalPages <= this.pageButtonsToShow;
    const isNearStart = currentPage <= Math.ceil(this.pageButtonsToShow / 2);
    const isNearEnd = currentPage + Math.floor(this.pageButtonsToShow / 2) >= this.totalPages;

    if (shouldShowAllPages) {
       this.renderedPages = this.createPageRange(1, this.totalPages);
       return this.renderedPages;
    }

    if (isNearStart) {
      this.renderedPages = this.createPageRange(1, this.pageButtonsToShow);
      return this.renderedPages;
    }

    if (isNearEnd) {
      const startOfEndRange = this.totalPages - this.pageButtonsToShow + 1
      this.renderedPages = this.createPageRange(startOfEndRange, this.totalPages);
      return this.renderedPages;
    }

    // default case
    const middleRangeStart  = currentPage - Math.floor(this.pageButtonsToShow / 2);
    this.renderedPages = this.createPageRange(middleRangeStart , middleRangeStart  + this.pageButtonsToShow - 1);
    return this.renderedPages;
  }

  private createPageRange(start: number, end: number): number[] {
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }
}
